import React, { memo, useMemo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { useCustomReports } from 'Context/CustomReports';
import { ReportCard } from '../ReportCard/ReportCard';
import classes from './recentTimecards.module.css';

interface UserHours {
  name: string;
  hours: number;
}

interface ProjectGroup {
  uid: string;
  users: UserHours[];
  totalHours: number;
}

function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0 && m === 0) return '0h';
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function RecentTimecards() {
  const { recentTimecards }: any = useCustomReports();
  const { data, loading, error } = recentTimecards;

  const projectGroups: ProjectGroup[] = useMemo(() => {
    if (!data || data.length === 0) return [];

    const projects = new Map<string, Map<string, number>>();

    data.forEach((row: any) => {
      const project = row['project.uid'] || 'Unassigned';
      const firstName = row['user.first_name'] || '';
      const lastName = row['user.last_name'] || '';
      const name = `${firstName} ${lastName}`.trim() || 'Unknown';
      const hours = parseFloat(row['timecard.total_hours']) || 0;

      if (!projects.has(project)) projects.set(project, new Map());
      const userMap = projects.get(project)!;
      userMap.set(name, (userMap.get(name) || 0) + hours);
    });

    return Array.from(projects.entries())
      .map(([uid, userMap]) => {
        const users = Array.from(userMap.entries())
          .map(([name, hours]) => ({ name, hours }))
          .sort((a, b) => b.hours - a.hours);
        const totalHours = users.reduce((sum, u) => sum + u.hours, 0);
        return { uid, users, totalHours };
      })
      .sort((a, b) => b.totalHours - a.totalHours);
  }, [data]);

  return (
    <ReportCard
      title="Recent Timecards"
      subtitle="Hours by project and team member (last 30 days)"
      loading={loading}
      error={error}
      configKey="recentTimecards"
    >
      {projectGroups.length > 0 ? (
        <div className={classes.groupList}>
          {projectGroups.map((group) => (
            <div key={group.uid} className={classes.projectGroup}>
              <div className={classes.projectHeader}>
                <span className={classes.projectUid}>{group.uid}</span>
                <span className={classes.projectTotal}>{formatHours(group.totalHours)}</span>
              </div>
              <div className={classes.userList}>
                {group.users.map((user) => (
                  <div key={user.name} className={classes.userRow}>
                    <span className={classes.userName}>{user.name}</span>
                    <span className={classes.userHours}>{formatHours(user.hours)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className={classes.emptyState}>No timecard data available.</p>
      )}
    </ReportCard>
  );
}

const RecentTimecardsMemo = memo(RecentTimecards, areEqual);
export { RecentTimecardsMemo as RecentTimecards };
